import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { NotificationFeedFilter } from './notification-feed-filter';
import { NotificationFeedFilterSelect } from './NotificationFeedFilterSelect';
import { NotificationFeedPreferencesLink } from './NotificationFeedPreferencesLink';

const titleMessage = defineMessage({
  id: 'notifications.feed.title',
  defaultMessage: 'Notificaciones',
});

const markAllReadMessage = defineMessage({
  id: 'notifications.feed.markAllRead',
  defaultMessage: 'Marcar todas como leídas',
});

const headerIconButtonSx = {
  p: 0.5,
  color: 'text.disabled',
  '&:hover': { color: 'text.secondary' },
  '& .MuiSvgIcon-root': { fontSize: 16 },
} as const;

export type NotificationFeedHeaderProps = {
  unreadCount: number;
  filter: NotificationFeedFilter;
  onFilterChange: (next: NotificationFeedFilter) => void;
  onMarkAllAsRead: () => void;
};

/**
 * Header for the MUI notifications feed with filter, preferences, and mark-all-read.
 */
export function NotificationFeedHeader({
  unreadCount,
  filter,
  onFilterChange,
  onMarkAllAsRead,
}: NotificationFeedHeaderProps) {
  const $t = useIntl().formatMessage;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        // Match list item padding so the settings icon aligns with archive.
        pl: 2,
        pr: 1,
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2">{$t(titleMessage)}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <NotificationFeedFilterSelect value={filter} onChange={onFilterChange} />
        {unreadCount > 0 && (
          <IconButton
            size="small"
            onClick={onMarkAllAsRead}
            title={$t(markAllReadMessage)}
            sx={headerIconButtonSx}
          >
            <DoneAllOutlinedIcon />
          </IconButton>
        )}
        <NotificationFeedPreferencesLink />
      </Box>
    </Box>
  );
}
